import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceRef,
  Signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AppConfigService } from '../../app-config.service';
import { ILayout } from '@web/dtos';
import { LayoutsGateway } from './layouts.gateway';
import { IApiResponse } from '@web/shared-types';

@Injectable({
  providedIn: 'root',
})
export class LayoutsStore {
  private readonly configService: AppConfigService = inject(AppConfigService);

  private readonly gateway: LayoutsGateway = new LayoutsGateway();

  readonly #layoutResource: ResourceRef<IApiResponse<ILayout[]> | undefined> =
    resource({
      loader: (): Promise<IApiResponse<ILayout[]>> => {
        return firstValueFrom(this.gateway.get());
      },
    });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#layoutResource.isLoading()
  );
  readonly layouts: Signal<ILayout[]> = computed((): ILayout[] => {
    return this.#layoutResource.hasValue()
      ? (this.#layoutResource.value()?.data ?? [])
      : [];
  });
  readonly layoutNames: Signal<string[]> = computed(() =>
    this.layouts().map((layout: ILayout) => layout.layoutName)
  );

  updateLayout(query: ILayout[]): void {
    this.#layoutResource.update((): IApiResponse<ILayout[]> => {
      return {
        data: query,
      };
    });
  }

  reload(): void {
    this.#layoutResource.reload();
  }

  upload(query: FormData): void {
    firstValueFrom(this.gateway.upload(query)).then(
      (result: IApiResponse<ILayout[]>): void => {
        if (result && result.data) {
          this.updateLayout(result.data);
        }
      }
    );
  }

  delete(query: ILayout): void {
    firstValueFrom(this.gateway.delete(query)).then(
      (result: IApiResponse<ILayout[]>): void => {
        if (result && result.data) {
          this.updateLayout(result.data);
        }
      }
    );
  }

  save(query: ILayout[]): void {
    firstValueFrom(this.gateway.save(query)).then(
      (result: IApiResponse<ILayout[]>): void => {
        if (result && result.data) {
          this.updateLayout(result.data);
        }
      }
    );
  }

  getThumbnailPath(id: string | undefined): string {
    return (
      this.configService.get().general.cmsUrl + '/layouts/' + id + '/thumbnail'
    );
  }

  getLayoutPath(id: string | undefined): string {
    return this.configService.get().general.cmsUrl + '/layouts/' + id;
  }
}
